import cookie from 'js-cookie';
import { useCallback } from 'react';

import type { BrowserStorageType } from './types';
import { session, local } from '../browserStorage';

const BROWSER_STORAGE_GETTERS = {
  cookies: (key: string) => cookie.get(key),
  localStorage: (key: string) => local.getItem(key) ?? undefined,
  sessionStorage: (key: string) => session.getItem(key) ?? undefined,
} as const satisfies Record<
  BrowserStorageType,
  (key: string) => string | undefined
>;

const BROWSER_STORAGE_SETTERS = {
  cookies: (key: string, value: string, options?: Cookies.CookieAttributes) => {
    cookie.set(key, value, options);
  },
  localStorage: (key: string, value: string) => {
    local.setItem(key, value);
  },
  sessionStorage: (key: string, value: string) => {
    session.setItem(key, value);
  },
} as const satisfies Record<
  BrowserStorageType,
  (key: string, value: string, options?: Cookies.CookieAttributes) => void
>;

interface UseBrowserStorageValueOptions {
  cookieAttributes?: Cookies.CookieAttributes;
}

export function useBrowserStorageValue(
  storageKey: string,
  storageType: BrowserStorageType,
  { cookieAttributes }: UseBrowserStorageValueOptions = {}
): [get: () => string | undefined, set: (value: string) => void] {
  const getBrowserStorageValue = useCallback(
    () => BROWSER_STORAGE_GETTERS[storageType](storageKey),
    [storageKey, storageType]
  );

  const setBrowserStorageValue = useCallback(
    (value: string) => {
      BROWSER_STORAGE_SETTERS[storageType](storageKey, value, cookieAttributes);
    },
    [storageKey, storageType, cookieAttributes]
  );

  return [getBrowserStorageValue, setBrowserStorageValue];
}
